import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import './Login.css';

function Login() {
  const [siteId, setSiteId] = useState('default');
  const [siteOptions, setSiteOptions] = useState([]);
  const [managementMode, setManagementMode] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [discordLoading, setDiscordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithDiscord, authNotice, clearAuthNotice } = useAuth();
  const { t } = useI18n();

  React.useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await fetch('/api/sites/public');
        const data = await response.json();
        if (response.ok && Array.isArray(data.sites)) {
          setSiteOptions(data.sites);
          if (data.sites.length > 0) {
            setSiteId(data.sites[0].siteId);
          }
        }
      } catch (err) {
        console.warn('Could not load sites for login:', err.message);
      }
    };
    fetchSites();
  }, []);

  // using i18n `t()` helper for labels/messages

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const targetSiteId = managementMode ? 'default' : siteId;
    const result = await login(targetSiteId, username, password);

    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }

    // If MFA required, set MFA flow state
    if (result.mfaRequired) {
      setMfaRequired(true);
      setTempToken(result.tempToken);
      setLoading(false);
      return;
    }
  };



  const handleDiscordLogin = async () => {
    setError('');
    setDiscordLoading(true);

    const result = await loginWithDiscord(siteId);
    if (!result.success) {
      setError(result.error);
      setDiscordLoading(false);
    }
  };

  // MFA state & handlers
  const { verifyMfa, passkeyLoginRequest, passkeyLoginVerify } = useAuth();
  const [mfaRequired, setMfaRequired] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);

  const submitMfa = async () => {
    setMfaError('');
    setMfaLoading(true);
    try {
      const res = await verifyMfa(tempToken, mfaCode);
      if (!res.success) {
        setMfaError(res.error || 'MFA verification failed');
      }
    } finally {
      setMfaLoading(false);
    }
    // on success, AuthContext will set token/user and App will switch view
  };

  // WebAuthn helpers
  const base64urlToBuffer = (base64url) => {
    if (!base64url) return base64url;
    if (base64url instanceof ArrayBuffer) return base64url;
    if (ArrayBuffer.isView(base64url)) {
      return base64url.buffer.slice(base64url.byteOffset, base64url.byteOffset + base64url.byteLength);
    }
    if (typeof base64url === 'object') {
      if (Array.isArray(base64url)) return Uint8Array.from(base64url).buffer;
      if (base64url.data !== undefined) return base64urlToBuffer(base64url.data);
      return base64url;
    }
    if (typeof base64url !== 'string') return base64url;
    const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
    const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
    const binary = atob(base64 + pad);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
    return bytes.buffer;
  };

  const bufferToBase64Url = (buffer) => {
    const bytes = new Uint8Array(buffer);
    let str = '';
    for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
    return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  };

  const handlePasskeySignIn = async () => {
    setError('');
    if (!username) {
      setError(t('app.auth.passkeyErrors.enterUsername') || 'Enter username for passkey login');
      return;
    }
    try {
      const start = await passkeyLoginRequest(username, managementMode ? 'default' : siteId);
      if (!start || !start.success) throw new Error(start.error || (t('app.auth.passkeyErrors.requestFailed') || 'Passkey request failed'));
      const options = start.options;
      options.challenge = base64urlToBuffer(options.challenge);
      if (Array.isArray(options.allowCredentials)) {
        options.allowCredentials = options.allowCredentials.map(c => ({ ...c, id: base64urlToBuffer(c.id) }));
      }
      const cred = await navigator.credentials.get({ publicKey: options });
      if (!cred) throw new Error(t('app.auth.passkeyErrors.noCredential') || 'No credential returned');

      const authResp = cred.response;
      const assertion = {
        id: cred.id,
        rawId: bufferToBase64Url(cred.rawId),
        response: {
          clientDataJSON: bufferToBase64Url(authResp.clientDataJSON),
          authenticatorData: bufferToBase64Url(authResp.authenticatorData),
          signature: bufferToBase64Url(authResp.signature),
          userHandle: authResp.userHandle ? bufferToBase64Url(authResp.userHandle) : null
        },
        type: cred.type
      };

      const verified = await passkeyLoginVerify(username, managementMode ? 'default' : siteId, assertion);
      if (!verified.success) {
        setError(verified.error || (t('app.auth.passkeyErrors.loginFailed') || 'Passkey login failed'));
      }
    } catch (err) {
      setError(err.message || String(err));
    }
  };

  return (
    <div className="login-container">
      <div className="login-management-switch">
        <button
          type="button"
          className={managementMode ? 'switch-btn active' : 'switch-btn'}
          onClick={() => setManagementMode(true)}
        >
          Tenant Management
        </button>
        <button
          type="button"
          className={!managementMode ? 'switch-btn active' : 'switch-btn'}
          onClick={() => setManagementMode(false)}
        >
          Server Login
        </button>
      </div>

      <div className="login-background">
        <div className="bg-pattern"></div>
        <div className="bg-glow"></div>
      </div>

      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <span className="logo-icon">V</span>
          </div>
          <h1 className="login-title">Void Development</h1>
          <p className="login-subtitle">{managementMode ? 'Management Login' : t('login.adminAccess')}</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="login-error">
              <span className="error-icon">!</span>
              <span className="error-message">{error}</span>
            </div>
          )}

          {authNotice && (
            <div className="login-info">
              <span className="error-icon">i</span>
              <span className="error-message">{authNotice}</span>
              <button type="button" className="close-info" onClick={clearAuthNotice}>×</button>
            </div>
          )}

          {!managementMode && (
            <div className="form-field">
              <label htmlFor="site">{t('login.selectServerLabel')}</label>
              <p className="field-hint">{t('login.selectServerHint')}</p>
              <div className="input-wrapper select-wrapper">
                <select
                  id="site"
                  value={siteId}
                  onChange={(e) => setSiteId(e.target.value)}
                  disabled={loading || siteOptions.length === 0}
                >
                  <option value="" disabled>
                    {siteOptions.length === 0 ? 'Loading servers...' : t('login.selectServerLabel')}
                  </option>
                  {siteOptions.map((site) => (
                    <option key={site.siteId} value={site.siteId}>{site.name}</option>
                  ))}
                </select>
                <span className="select-chevron" aria-hidden="true">▾</span>
              </div>
            </div>
          )}

          <div className="form-field">
            <label htmlFor="username">{t('login.username')}</label>
            <div className="input-wrapper">
              <span className="input-icon">*</span>
              <input
                id="username"
                type="text"
                placeholder={t('login.usernamePlaceholder')}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                autoComplete="username"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-field">
            <label htmlFor="password">{t('login.password')}</label>
            <div className="input-wrapper">
              <span className="input-icon">*</span>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder={t('login.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={loading}
              />
              <button
                type="button"
                className="toggle-password"
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                *
              </button>
            </div>
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? (
              <>
                <span className="button-spinner"></span>
                {t('login.authenticating')}
              </>
            ) : (
              <>
                <span>{t('login.signIn')}</span>
                <span className="button-arrow">→</span>
              </>
            )}
          </button>
          <button type="button" className="discord-login-button" onClick={handleDiscordLogin} disabled={discordLoading || loading || !siteId}>
            {discordLoading ? t('login.discordConnecting') : `${t('login.continueDiscord')}`}
          </button>

              <button type="button" className="passkey-login-button cp-btn-primary" onClick={handlePasskeySignIn} disabled={loading || !username || !siteId}>
                {t('app.auth.passkeySignIn') || 'Sign in with Passkey'}
              </button>

          {mfaRequired && (
              <div className="mfa-panel" onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitMfa();
              }
            }}>
              <div className="mfa-title">{t('app.auth.mfaPanel.title') || 'Multi-factor verification'}</div>
              <div className="mfa-help">{t('app.auth.mfaPanel.help') || 'Enter the 6-digit code from your authenticator app.'}</div>
              <label className="mfa-label">{t('app.auth.mfaPanel.label') || 'MFA Code'}</label>
              <input
                className="mfa-input"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="123456"
                autoFocus
              />
              {mfaError && <div className="login-error"><span className="error-message">{mfaError}</span></div>}
              <div className="mfa-actions">
                <button type="button" className="cp-btn-secondary" onClick={() => { setMfaRequired(false); setTempToken(null); setMfaCode(''); }}>{t('app.auth.mfaPanel.cancel') || 'Cancel'}</button>
                <button type="button" className="cp-btn-primary" onClick={submitMfa} disabled={mfaLoading}>{mfaLoading ? (t('app.auth.mfaPanel.verifying') || 'Verifying...') : (t('app.auth.mfaPanel.verify') || 'Verify')}</button>
              </div>
            </div>
          )}
        </form>

        <div className="login-footer">
          <div className="security-badge">
            <i className="fa-solid fa-lock badge-icon" aria-hidden="true"></i>
            <span className="badge-text">{t.secureAuth}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
